import { AlternativeEditorDialogViewModel } from '@Alternatives/Alternatives/Components/AlternativeEditorDialog';
import AlternativeEditorDialog from '@Alternatives/Alternatives/Components/AlternativeEditorDialog.vue';
import { ChangeStatusCommandV1, CreateAlternativeCommandV1, DeleteAlternativeCommandV1, UpdateAlternativeAttributeValueCommandV1, UpdateAlternativeCommandV1 } from '@Alternatives/Alternatives/Domain/commands-core';
import AlternativesCommands from '@Alternatives/Alternatives/Domain/commands-vue-component';
import AlternativesQueries from '@Alternatives/Alternatives/Domain/queries-vue-component';
import { AlternativeReadModel, AlternativesReadModel } from '@Alternatives/Alternatives/Domain/readmodels';
import { AlternativesComparisonDialogViewModel } from '@Alternatives/AlternativesLists/Components/AlternativesComparisonDialog';
import AlternativesComparisonDialog from '@Alternatives/AlternativesLists/Components/AlternativesComparisonDialog.vue';
import { ViewSettingsEditorViewModel } from '@Alternatives/AlternativesLists/Components/ViewSettingsEditor';
import ViewSettingsEditor from '@Alternatives/AlternativesLists/Components/ViewSettingsEditor.vue';
import { DeleteAlternativesCommandV1, ExportAlternativesCommandV1, UpdateMultipleAlternativesAttributeValueCommandV1, UpdateMultipleAlternativesStatusCommandV1 } from '@Alternatives/AlternativesLists/Domain/commands-core';
import AlternativesListsCommands from '@Alternatives/AlternativesLists/Domain/commands-vue-component';
import AlternativesListsQueries from '@Alternatives/AlternativesLists/Domain/queries-vue-component';
import { AlternativesComparisonReadModel, KanbanPageReadModel } from '@Alternatives/AlternativesLists/Domain/readmodels';
import { AttributeReadModel } from '@Alternatives/Attributes/Domain/readmodels';
import { AttributeUtils } from '@Alternatives/Attributes/utils';
import { convertToDate, dateCompare, dateCompare1, getDistinctBgCss, numCompare, strCompareLowerCase } from '@common/Common';
import { ListSelectionDialogViewModel } from '@common/Components/ListSelectionDialog';
import ListSelectionDialog from '@common/Components/ListSelectionDialog.vue';
import { MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog';
import MonthPickerDialog from '@common/Components/MonthPickerDialog.vue';
import { NumericInputDialogViewModel } from '@common/Components/NumericInputDialog';
import NumericInputDialog from '@common/Components/NumericInputDialog.vue';
import PortfolioNavigation from "@common/Components/PortfolioNavigation.vue";
import { MonthYear } from '@common/types';
import Component, { mixins } from 'vue-class-component';


@Component({
    components: { PortfolioNavigation, ViewSettingsEditor, ListSelectionDialog, NumericInputDialog, MonthPickerDialog, AlternativesComparisonDialog, AlternativeEditorDialog }
})
export default class AlternativesKanbanPage extends mixins(AlternativesListsQueries, AlternativesListsCommands, AlternativesQueries, AlternativesCommands)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId() { return Number.parseInt(this.$route.params.projectId); }

    searchPhrase: string | null = null;
    columns: ColumnViewModel[] = null;
    colorInfoMap: Record<string, ColorInfo> = {};
    viewSettingsEditor: ViewSettingsEditorViewModel = new ViewSettingsEditorViewModel(this.projectId);
    statusListSelectionDialog: ListSelectionDialogViewModel = new ListSelectionDialogViewModel();
    attrListSelectionDialog: ListSelectionDialogViewModel = new ListSelectionDialogViewModel();
    numericInputDialog: NumericInputDialogViewModel = new NumericInputDialogViewModel();

    startDateMonthPicker: MonthPickerDialogViewModel = new MonthPickerDialogViewModel();
    commitmentDateMonthPicker: MonthPickerDialogViewModel = new MonthPickerDialogViewModel();
    alternativesComparisonDialog: AlternativesComparisonDialogViewModel = new AlternativesComparisonDialogViewModel();
    alternativeEditorDialog: AlternativeEditorDialogViewModel = new AlternativeEditorDialogViewModel();

    rm: KanbanPageReadModel | null = null;

    get colorAttributeId(): number | null { return this.viewSettingsEditor.colorAttributeId; }
    get columnAttributeId(): number | null { return this.viewSettingsEditor.columnAttributeId; }
    get viewDefId(): string | null { return this.viewSettingsEditor.viewDefId; }
    get formId(): number | null { return this.viewSettingsEditor.formId; }
    get orderBy(): string { return this.viewSettingsEditor.orderBy; }
    get orderType(): string { return this.viewSettingsEditor.orderType; }

    get viewDefName() { return this.rm.Views.find(v => v.Id === this.viewDefId)?.Name ?? 'All'; }
    get attributes() { return this.rm?.Attributes.filter(a => a.IsMultivalue === false); }
    get bulkEditablelistAttributesBySection() { return AttributeUtils.groupAndSort(this.attributes?.filter(a => !a.IsMetric).filter(a => a.IsList || (a.IsNumeric && a.IsManual))); }
    get attributesBySection() { return AttributeUtils.groupAndSort(this.attributes); }
    get selectedAlternatives() { return this.rm.Alternatives?.filter(a => a.IsSelected); }
    get alternativesView(): AlternativeReadModel[]
    {
        if (this.rm.Alternatives == null)
        {
            return [];
        }

        let view = new Array(...this.rm.Alternatives);

        if (this.searchPhrase)
        {
            const phrase = this.searchPhrase.toLowerCase();
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1);
        }

        if (this.orderBy === "name")
        {
            view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }
        else if (this.orderBy === "create date")
        {
            view = view.sort((a, b) => dateCompare1(a.Created, b.Created));
        }
        else if (this.orderBy === "modified")
        {
            view = view.sort((a, b) => dateCompare1(a.Modified, b.Modified));
        }
        else
        {
            const attributeId = parseInt(this.orderBy);
            const attr = this.attributes.find(a => a.Id === attributeId);

            if (attr)
            {
                const getOrder = Utils.buildOrderFn(attr);
                view = view.sort(getOrder);
            }
        }

        if (this.orderType !== "asc")
        {
            view = view.reverse();
        }

        return view;
    }
    get orderByLabel(): string | null
    {
        if (this.viewSettingsEditor && this.orderBy)
        {
            if (this.orderBy === 'name')
            {
                return `Name (${this.orderType})`;
            }
            else if (this.orderBy === 'modified')
            {
                return `Modified (${this.orderType})`;
            }
            else if (this.orderBy === 'create date')
            {
                return `Create date (${this.orderType})`;
            }
            else 
            {
                const attrId = parseInt(this.orderBy);
                const attrName = this.attributes?.find(a => a.Id === attrId)?.Name;
                return `${attrName} (${this.orderType})`;
            }
        }

        return null;
    }

    getColorCssClass(alternativeId: string): string { return this.colorInfoMap[alternativeId]?.cssClass || "bg-light text-dark"; }
    getColorLabel(alternativeId: string): string | null { return this.colorInfoMap[alternativeId]?.label || null; }
    selectAll() { this.rm.Alternatives.forEach(a => a.IsSelected = true); }
    unselectAll() { this.rm.Alternatives.forEach(a => a.IsSelected = false); }
    invertSelection() { this.rm.Alternatives.forEach(a => a.IsSelected = !a.IsSelected); }
    collapseAllColumns() { this.columns.forEach(a => a.IsCollapsed = true); }
    expandAllColumns() { this.columns.forEach(a => a.IsCollapsed = false); }

    buildKanbanColumns() 
    {
        if (this.columnAttributeId) 
        {
            const attr = this.attributes.find(a => a.Id === this.columnAttributeId);

            if (attr?.DefinedValues) 
            {
                const columns = attr.DefinedValues.map(v => new ColumnViewModel(AttributeUtils.convertUnknownValue(attr, v.Value), v.Name, this));
                columns.unshift(new ColumnViewModel(null, "Not set", this));
                this.columns = columns;
            }
        }
        else
        {
            this.columns = this.rm.Statuses.map(s => new ColumnViewModel(s.Id, s.Name, this));
        }
    }

    updateAlternativeColors()
    {
        const attr = this.attributes.find(a => a.Id === this.colorAttributeId) ?? null;
        this.colorInfoMap = Utils.buildAlternativeColorsMap(attr, this.rm.Alternatives);
    }

    setOrderBy(by: string)
    {
        this.viewSettingsEditor.setOrderBy(by)
    }

    setViewDef(viewDefId: string | null)
    {
        if (this.viewSettingsEditor.setViewDefId(viewDefId))
        {
            this.reloadAlternatives();
        }
    }


    showViewSettingsEditor()
    {
        this.viewSettingsEditor.show(this.rm.AlternativesLabel, this.attributes, this.rm.Forms);
    }

    showAlternativeCreator()
    {
        this.alternativeEditorDialog.showCreator(this.workspaceId, this.projectId, this.rm.Id, this.formId, this.rm.AlternativeLabel);
    }

    showAlternativeEditor(alternativeId: string)
    {
        this.alternativeEditorDialog.showEditor(this.workspaceId, this.projectId, this.rm.Id, alternativeId, this.formId, this.rm.AlternativeLabel);
    }

    showSelectedAlternativesComparison() 
    {
        this.alternativesComparisonDialog.show();
        this.runAlternativesComparisonQueryV1(this.workspaceId, this.projectId, this.viewDefId, this.selectedAlternatives.map(a => a.Id), this.formId);
    }

    showBulkStatusEditor()
    {
        this.statusListSelectionDialog.valueRequired = true;
        const items = this.rm.Statuses.map(a => { return { key: a.Id.toString(), value: a.Id.toString(), label: a.Name, description: null } });
        this.statusListSelectionDialog.show(null, items);
    }

    showBulkAttributeValueEditor(attr: AttributeReadModel)
    {
        if (attr.IsList)
        {
            this.attrListSelectionDialog.valueRequired = false;
            const items = attr.DefinedValues.map(a => { return { key: a.Value.toString(), value: a.Value.toString(), label: a.Name, description: a.Description } });
            this.attrListSelectionDialog.show(attr.Id.toString(), items);
        }
        else
        {
            this.numericInputDialog.id = attr.Id.toString();
            this.numericInputDialog.valueRequired = true;
            this.numericInputDialog.label = attr.Name;
            this.numericInputDialog.description = attr.Description;
            this.numericInputDialog.minValue = attr.MinValue;
            this.numericInputDialog.maxValue = attr.MaxValue;
            this.numericInputDialog.show();
        }
    }

    showBulkStartDateMonthPicker()
    {
        this.startDateMonthPicker.show(null, 'Edit start date', false);
    }

    showBulkCommitmentDateMonthPicker()
    {
        this.commitmentDateMonthPicker.show(null, 'Edit commitment date', false);
    }

    reloadAlternatives()
    {
        this.rm.Alternatives = null;
        this.runAlternativesByViewDefIdQueryV1(this.workspaceId, this.projectId, this.viewDefId);
    }


    onViewSettingsUpdated()
    {
        this.updateAlternativeColors();
        this.buildKanbanColumns();
    }

    onDragOverContainer(event: DragEvent)
    {
        const container = event.currentTarget as HTMLElement;
        if (!container) return;  // Ensure container is not null

        const rect = container.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;

        const scrollMargin = 150;
        const scrollStep = 10;

        if (offsetX < scrollMargin)
        {
            container.scrollLeft -= scrollStep;
        }
        else if (offsetX > rect.width - scrollMargin)
        {
            container.scrollLeft += scrollStep;
        }
    }

    onDragStart(e: DragEvent, alternative: AlternativeReadModel)
    {
        e.dataTransfer.setData('application/json', alternative.Id);
    }

    onDrop(event: DragEvent, column: ColumnViewModel)
    {
        const data = event.dataTransfer.getData('application/json');
        const alternative = this.rm.Alternatives.find(a => a.Id === data);

        if (!column.alternativesView.includes(alternative))
        {
            if (this.columnAttributeId == null)
            {
                this.handleChangeStatusCommand(alternative, Number(column.key));
            }
            else
            {
                this.handleSetAlternativeAttributeValueCommand(alternative, column.key);
            }
        }

        column.dragOver = false;
    }

    onDragEnter(event: DragEvent, column: ColumnViewModel)
    {
        column.dragOver = true;
    }

    onDragLeave(event: DragEvent, column: ColumnViewModel)
    {
        column.dragOver = false;
    }


    handleSetAlternativeAttributeValueCommand(rm: AlternativeReadModel, value: any)
    {
        const currValue = rm.AttributeValues.find(v => v.AttributeId === this.columnAttributeId)?.Value?.toString();
        this.invokeUpdateAlternativeAttributeValueCommandV1(this.rm.UserId, this.workspaceId, this.projectId, this.rm.Id, rm.Id, rm.AlternativeId, this.columnAttributeId, currValue, value)
    }

    handleChangeStatusCommand(rm: AlternativeReadModel, statusId: number)
    {
        this.invokeChangeStatusCommandV1(this.rm.UserId, this.workspaceId, this.projectId, this.rm.Id, rm.Id, rm.AlternativeId, statusId);
    }

    handleMultipleAlternativesAttributeValueUpdateCommand(value: string, attrId: string)
    {
        const ids = this.selectedAlternatives.map(a => a.Id);
        this.invokeUpdateMultipleAlternativesAttributeValueCommandV1(this.rm.UserId, this.workspaceId, this.projectId, this.rm.Id, ids, Number(attrId), value);
    }

    handleUpdateMultipleAlternativesStartDateCommand(date: MonthYear | null)
    {
        const ids = this.selectedAlternatives.map(a => a.Id);
        this.invokeUpdateMultipleAlternativesStartDateCommandV1(this.workspaceId, this.projectId, this.rm.Id, ids, date)
    }

    handleUpdateMultipleAlternativesCommitmentDateCommand(date: MonthYear | null)
    {
        const ids = this.selectedAlternatives.map(a => a.Id);
        this.invokeUpdateMultipleAlternativesCommitmentDateCommandV1(this.workspaceId, this.projectId, this.rm.Id, ids, date)
    }

    handleUpdateMultipleAlternativesStatusCommand(value: string)
    {
        const statusId = Number(value);

        if (statusId)
        {
            //if (confirm("Are you sure you want to move the selected items?"))
            {
                const ids = this.selectedAlternatives.map(a => a.Id);
                this.invokeUpdateMultipleAlternativesStatusCommandV1(this.rm.UserId, this.workspaceId, this.projectId, this.rm.Id, ids, statusId);
            }
        }
    }

    handleDeleteAlternativesCommand()
    {
        if (confirm("Are you sure you want to delete the selected items? "))
        {
            const ids = this.selectedAlternatives.map(a => a.Id);
            this.invokeDeleteAlternativesCommandV1(this.workspaceId, this.projectId, this.rm.Id, ids, this.rm.UserId);
        }
    }

    handleExportAlternativesCommand()
    {
        this.invokeExportAlternativesCommandV1(this.workspaceId, this.rm.Id, this.viewDefId);
    }


    mounted()
    {
        document.title = "Loading | TransparentChoice";

        const defaultViewDefId = this.viewSettingsEditor.viewDefId ?? null;
        this.viewSettingsEditor.setViewDefId(this.$route.params.viewDefId ?? defaultViewDefId);
        this.runKanbanPageQueryV1(this.workspaceId, this.projectId, this.viewDefId);
    }

    onSetModel(model: any, modelName: string)
    {
        if (modelName === "KanbanPageReadModel")
        {
            this.rm = model as KanbanPageReadModel;
            document.title = `${this.rm.AlternativesLabel} | TransparentChoice`;

            this.buildKanbanColumns();
            this.updateAlternativeColors();
        }
        else if (modelName === "AlternativesReadModel")
        {
            const rm = model as AlternativesReadModel;
            this.rm.Alternatives = rm.Alternatives;
            this.updateAlternativeColors();
            this.alternativeEditorDialog.close();
        }
        else if (modelName === "AlternativesComparisonReadModel")
        {
            const rm = model as AlternativesComparisonReadModel;
            this.alternativesComparisonDialog.setData(rm);
        }
    }

    onCommandSucceed(cmd: any, correlationId: string, result: any)
    {
        if (ExportAlternativesCommandV1.matchType(cmd)) 
        {
            if (result) 
            {
                location.href = result;
            }
        }
        else if (DeleteAlternativesCommandV1.matchType(cmd)) 
        {
            const cmd1 = cmd as DeleteAlternativesCommandV1;
            this.rm.Alternatives = this.rm.Alternatives.filter(a => !cmd1.AlternativeIds.includes(a.Id));
        }
        else if (DeleteAlternativeCommandV1.matchType(cmd)) 
        {
            this.rm.Alternatives = this.rm.Alternatives.filter(a => a.Id !== correlationId);
        }
        else if (UpdateMultipleAlternativesStatusCommandV1.matchType(cmd)) 
        {
            const cmd1 = cmd as UpdateMultipleAlternativesStatusCommandV1;
            const statusId = Number(cmd1.StatusId);
            this.rm.Alternatives.filter(a => cmd1.AlternativeIds.includes(a.Id)).forEach(a => a.StatusId = statusId);
            this.statusListSelectionDialog.close();
        }
        else if (UpdateMultipleAlternativesAttributeValueCommandV1.matchType(cmd)) 
        {
            this.attrListSelectionDialog.close();
            if (this.numericInputDialog) this.numericInputDialog.isOpened = false;
            this.reloadAlternatives();
        }
        else if (ChangeStatusCommandV1.matchType(cmd)) 
        {
            const cmd1 = cmd as ChangeStatusCommandV1;
            this.rm.Alternatives.find(a => a.Id === correlationId).StatusId = Number(cmd1.NewStatusId);
        }
        else if (UpdateAlternativeAttributeValueCommandV1.matchType(cmd)) 
        {
            this.reloadAlternatives();
        }
        else if (CreateAlternativeCommandV1.matchType(cmd)) 
        {
            this.reloadAlternatives();
        }
        else if (UpdateAlternativeCommandV1.matchType(cmd)) 
        {
            this.reloadAlternatives();
        }
    }
}

class ColumnViewModel
{
    page: AlternativesKanbanPage;
    IsCollapsed: boolean = true;
    dragOver: boolean = false;
    label: string = null;
    key: string | number | Date | null = null;

    get title() { return `${this.label} (${this.alternativesView?.length})`; }

    get alternativesView() 
    {
        if (!this.page.alternativesView) return [];

        if (this.page.columnAttributeId) 
        {
            //const noValueKey = "__noValue__";

            return this.page.alternativesView.filter(a =>
            {
                const attributeValue = a.AttributeValues.find(av => av.AttributeId === this.page.columnAttributeId);
                const key = attributeValue && attributeValue.HasValue ? attributeValue.Value : null;
                return this.key == key;
            });
        }
        else
        {
            return this.page.alternativesView.filter(a => a.StatusId === this.key);
        }
    }

    constructor(key: string | number | Date | null, label: string, page: AlternativesKanbanPage)
    {
        this.page = page;
        this.label = label;
        this.key = key;
    }

    selectAll() { this.alternativesView.forEach(a => a.IsSelected = true); }

    unselectAll() { this.alternativesView.forEach(a => a.IsSelected = false); }

    invertSelection() { this.alternativesView.forEach(a => a.IsSelected = !a.IsSelected); }
}

type ColorInfo = {
    cssClass: string,
    label: string | null
};

class Utils
{
    static buildOrderFn(attr: AttributeReadModel)
    {
        if (attr.IsString)
        {
            return function (a: AlternativeReadModel, b: AlternativeReadModel): number
            {
                const attrValA = String(a.AttributeValues.find(av => av.AttributeId === attr.Id)?.Value);
                const attrValB = String(b.AttributeValues.find(av => av.AttributeId === attr.Id)?.Value);
                return strCompareLowerCase(attrValA, attrValB);
            };
        }
        else if (attr.IsNumeric)
        {
            return function (a: AlternativeReadModel, b: AlternativeReadModel): number
            {
                const attrValA = Number(a.AttributeValues.find(av => av.AttributeId === attr.Id)?.Value);
                const attrValB = Number(b.AttributeValues.find(av => av.AttributeId === attr.Id)?.Value);
                return numCompare(attrValA, attrValB);
            };
        }
        else if (attr.IsDate)
        {
            return function (a: AlternativeReadModel, b: AlternativeReadModel): number
            {
                const attrValA = convertToDate(a.AttributeValues.find(av => av.AttributeId === attr.Id)?.Value);
                const attrValB = convertToDate(b.AttributeValues.find(av => av.AttributeId === attr.Id)?.Value);
                return dateCompare(attrValA, attrValB);
            };
        }

        return null;
    }

    static buildAlternativeColorsMap(attr: AttributeReadModel, alternatives: AlternativeReadModel[]): Record<string, ColorInfo>
    {
        const colorInfoMap: Record<string, ColorInfo> = {};

        if (attr == null)
        {
            return colorInfoMap;
        }

        const valueToColorInfoMap: Record<string, ColorInfo> = {};

        if (attr && attr.DefinedValues)
        {
            attr.DefinedValues.forEach((definedValue, index) =>
            {
                const key = definedValue.Value.toString();

                valueToColorInfoMap[key] =
                {
                    cssClass: getDistinctBgCss(index),
                    label: definedValue.Name
                };
            });
        }

        alternatives.forEach(a =>
        {
            const value = a.AttributeValues.find(av => av.AttributeId === attr.Id)?.Value?.toString();
            const colorInfo = valueToColorInfoMap[value] || { cssClass: "bg-light text-dark", label: null };

            colorInfoMap[a.Id] = colorInfo;
        });

        return colorInfoMap;
    }
}